/**
 * Badge da inserire nei siti degli associati (pagina /area-riservata/badge e anteprima
 * nel Press Kit). Funzioni pure: nomi dei file, link di destinazione con tracciamento
 * e codice HTML da incollare.
 *
 * L'origine è sempre la produzione: i siti degli associati devono puntare a
 * studi.unasca.it anche se il codice viene copiato da staging o dal locale.
 * I nomi dei file non vanno mai cambiati dopo il rilascio: i siti li richiamano.
 */

export type FamigliaBadge = 'associato' | 'rete-certificata';
export type FormatoBadge = 'orizzontale' | 'quadrato' | 'compatto';
export type TemaBadge = 'chiaro' | 'scuro';

export const ORIGINE_BADGE = 'https://studi.unasca.it';

export const FORMATI: Record<FormatoBadge, { larghezza: number; altezza: number; etichetta: string }> = {
  orizzontale: { larghezza: 300, altezza: 100, etichetta: 'Orizzontale' },
  quadrato: { larghezza: 160, altezza: 160, etichetta: 'Quadrato' },
  compatto: { larghezza: 200, altezza: 56, etichetta: 'Compatto' }
};

export const fileBadge = (famiglia: FamigliaBadge, formato: FormatoBadge, tema: TemaBadge): string =>
  `/badge/${famiglia}-${formato}-${tema}.png`;

export const altBadge = (famiglia: FamigliaBadge): string =>
  famiglia === 'associato' ? 'Associato UNASCA' : 'Associato UNASCA · Rete Certificata';

export const titoloBadge = (famiglia: FamigliaBadge): string =>
  famiglia === 'associato' ? 'Membro associato UNASCA' : 'Studio della Rete Certificata UNASCA';

const tracciamento = (famiglia: FamigliaBadge): string =>
  `utm_source=badge&utm_medium=sito-associato&utm_campaign=${famiglia}`;

export const urlDestinazioneBadge = (famiglia: FamigliaBadge, idScheda?: string): string => {
  if (famiglia === 'associato') return `${ORIGINE_BADGE}/?${tracciamento(famiglia)}`;
  if (!idScheda) throw new Error('Il badge della Rete Certificata richiede la scheda dello studio');
  return `${ORIGINE_BADGE}/rete-certificata?studio=${encodeURIComponent(idScheda)}&${tracciamento(famiglia)}`;
};

const attributo = (valore: string): string =>
  valore.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const codiceBadge = (
  famiglia: FamigliaBadge,
  formato: FormatoBadge,
  tema: TemaBadge,
  idScheda?: string
): string => {
  const { larghezza, altezza } = FORMATI[formato];
  const href = attributo(urlDestinazioneBadge(famiglia, idScheda));
  const src = `${ORIGINE_BADGE}${fileBadge(famiglia, formato, tema)}`;
  return (
    `<a href="${href}" target="_blank" rel="noopener" title="${attributo(titoloBadge(famiglia))}">` +
    `<img src="${src}" width="${larghezza}" height="${altezza}" alt="${attributo(altBadge(famiglia))}" loading="lazy"></a>`
  );
};

export const codiceLinkTestuale = (famiglia: FamigliaBadge, idScheda?: string): string =>
  `<a href="${attributo(urlDestinazioneBadge(famiglia, idScheda))}" target="_blank" rel="noopener">${titoloBadge(famiglia)}</a>`;

/** Testo da girare a chi cura il sito dello studio: istruzioni brevi + codice. */
export const testoPerWebmaster = (codice: string): string =>
  [
    'Ciao,',
    '',
    'ti chiedo di inserire nel sito del nostro studio il badge ufficiale UNASCA.',
    'Basta incollare il codice qui sotto, senza modificarlo, nel punto in cui deve comparire',
    '(di solito il footer / piè di pagina di tutte le pagine, oppure la pagina «Chi siamo»).',
    "Con WordPress si usa un blocco «HTML personalizzato», con Wix «Incorpora codice» → «Incorpora HTML».",
    "L'immagine è ospitata su studi.unasca.it e resta sempre aggiornata: non serve scaricarla.",
    '',
    'Codice da incollare:',
    '',
    codice,
    '',
    'Grazie!'
  ].join('\n');

/** Link «mailto:» che apre un'email già scritta per il webmaster. */
export const mailtoWebmaster = (codice: string): string =>
  `mailto:?subject=${encodeURIComponent('Badge UNASCA da inserire nel sito')}&body=${encodeURIComponent(testoPerWebmaster(codice))}`;
